import { cleanAdminUserFormFields } from '../domain/adminUser/cleanAdminUserFormFields';
import {
  enrollmentsAPI,
  interviewReadinessAPI,
  mastersAPI,
  testimoniesAPI,
  usersAPI,
} from '../services/api';

function pickEnrollmentFields(formData) {
  const fields = {};
  const batchStatus = formData.studentStatus || formData.batchStatus;

  if (formData.batchId != null && String(formData.batchId).trim() !== '') {
    fields.batchId = String(formData.batchId);
  }
  if (batchStatus != null && String(batchStatus).trim() !== '') {
    fields.batchStatus = batchStatus;
    fields.studentStatus = batchStatus;
  }
  if (formData.placementStatus != null && String(formData.placementStatus).trim() !== '') {
    fields.placementStatus = formData.placementStatus;
  }
  if (formData.attendancePercentage != null && String(formData.attendancePercentage).trim() !== '') {
    fields.attendancePercentage = formData.attendancePercentage;
  }
  if (formData.courseCompletionDate) {
    fields.courseCompletionDate = formData.courseCompletionDate;
  }
  if (formData.batchStartDate) {
    fields.batchStartDate = formData.batchStartDate;
  }
  if (formData.remarks != null && String(formData.remarks).trim() !== '') {
    fields.remarks = formData.remarks;
  }
  for (const key of [
    'insuranceTaken',
    'insuranceDeactivated',
    'certificationIssued',
    'videoTestimonyReceived',
    'testimonyReceived',
    'appliedForTruechip',
    'truechipResult',
  ]) {
    if (formData[key] != null && String(formData[key]).trim() !== '') {
      fields[key] = formData[key];
    }
  }
  return fields;
}

/**
 * Build usersAPI.update payload from admin edit form data (shared with Edit User page).
 * @param {object} formData
 * @param {object} options
 * @param {string|null} options.companyId - resolved placement company id
 * @returns {object}
 */
export function buildAdminUserUpdatePayload(formData, { companyId = null } = {}) {
  const placementData = formData.placement
    ? {
        company: companyId || formData.placement.companyName,
        companyId: companyId || formData.placement.companyName,
        position: formData.placement.position,
        jobRole: formData.placement.position,
        package: formData.placement.package ? parseFloat(formData.placement.package) : null,
        location: formData.placement.location,
        status:
          formData.placement.result && formData.placement.result.trim()
            ? String(formData.placement.result).replace(/\s+/g, '_')
            : null,
        offerDate: formData.placement.interviewDate
          ? new Date(formData.placement.interviewDate)
          : null,
        placementDate: formData.placement.placementDate
          ? new Date(formData.placement.placementDate)
          : null,
        joiningDate: formData.placement.joiningDate
          ? new Date(formData.placement.joiningDate)
          : null,
        offerDetails: formData.placement.offerDetails || formData.placement.result || null,
        feedbackRemarks: formData.placement.location
          ? `Location: ${formData.placement.location}${
              formData.placement.offerDetails || formData.placement.result
                ? ` | ${formData.placement.offerDetails || formData.placement.result}`
                : ''
            }`
          : formData.placement.offerDetails || formData.placement.result || null,
        shouldMoveToAlumni: formData.placement.shouldMoveToAlumni || 'Yes',
        placementGroupRemovalReason: formData.placement.placementGroupRemovalReason || null,
        removedFromPlacementGroup:
          formData.placement.shouldMoveToAlumni === 'No' ||
          formData.placement.shouldMoveToAlumni === false,
      }
    : null;

  const enrollmentFields = pickEnrollmentFields(formData);
  const userData = { ...formData };
  cleanAdminUserFormFields(userData, 'null');

  if (userData.country !== undefined) {
    userData.countryId =
      userData.country === '' || userData.country === null ? null : String(userData.country);
    delete userData.country;
  }
  if (userData.state !== undefined) {
    userData.stateId =
      userData.state === '' || userData.state === null ? null : String(userData.state);
    delete userData.state;
  }
  if (userData.countryId === '' || userData.countryId === undefined) userData.countryId = null;
  if (userData.stateId === '' || userData.stateId === undefined) userData.stateId = null;
  if (userData.cityId === '' || userData.cityId === undefined) userData.cityId = null;

  delete userData.interviewReadiness;
  delete userData.testimony;
  delete userData.additionalEnrollments;

  const submitData = {
    ...userData,
    ...enrollmentFields,
    middleName: userData.middleName != null ? String(userData.middleName) : '',
    profilePreference:
      userData.profilePreference && userData.profilePreference.trim()
        ? String(userData.profilePreference).replace(/\s+/g, '_')
        : userData.profilePreference || null,
    placement: placementData,
    batchStatus: enrollmentFields.batchStatus || userData.studentStatus || userData.batchStatus,
    studentStatus: enrollmentFields.studentStatus || userData.studentStatus || userData.batchStatus,
  };

  if (submitData.studentStatus === 'On Hold' && submitData.holdTillDate) {
    const holdRemark = `Hold till: ${submitData.holdTillDate}`;
    submitData.remarks = submitData.remarks
      ? `${submitData.remarks}\n${holdRemark}`
      : holdRemark;
  }

  return submitData;
}

/** Map Yes/No/empty to boolean for API fields that expect boolean. */
export function toOptionalBoolean(value) {
  if (value === 'Yes' || value === true || value === 'true') return true;
  if (value === 'No' || value === false || value === 'false') return false;
  return undefined;
}

/**
 * Build interview readiness upsert payload from form nested object.
 */
export function buildInterviewReadinessPayload(interviewReadiness) {
  if (!interviewReadiness?.enrollmentId) return null;
  const ir = interviewReadiness;
  const payload = {};
  const boolFields = [
    'interviewReady',
    'allModulesComplete',
    'projectSubmitted',
    'studentReadiness',
    'trainerReadiness',
    'resumeReadySubmitted',
    'resumeReviewed',
    'resumeChangesNeeded',
    'mockInterviewDone',
    'inPlacementGroup',
    'inMockGroup',
    'inUnderpreparedGroup',
  ];
  for (const key of boolFields) {
    const v = toOptionalBoolean(ir[key]);
    if (v !== undefined) payload[key] = v;
  }
  if (ir.numberOfMockInterviews !== '' && ir.numberOfMockInterviews != null) {
    payload.numberOfMockInterviews = parseInt(String(ir.numberOfMockInterviews), 10);
  }
  if (ir.resumeSubmissionDeadline) {
    payload.resumeSubmissionDeadline = ir.resumeSubmissionDeadline;
  }
  for (const key of [
    'communicationSkillsRating',
    'attitudeRating',
    'presentationRating',
    'technicalReadinessRating',
    'areasToImprove',
    'feedbackScore',
  ]) {
    if (ir[key] !== '' && ir[key] != null) {
      payload[key] = key.endsWith('Rating')
        ? parseInt(String(ir[key]), 10)
        : ir[key];
    }
  }
  return Object.keys(payload).length > 0 ? payload : null;
}

/**
 * Build testimony upsert payload from form nested object.
 */
export function buildTestimonyPayload(testimony) {
  if (!testimony?.enrollmentId) return null;
  const payload = {};
  const v1 = toOptionalBoolean(testimony.videoTestimonyReceived);
  const v2 = toOptionalBoolean(testimony.textTestimonyReceived);
  const v3 = toOptionalBoolean(testimony.wantToContributeAsTrainer);
  if (v1 !== undefined) payload.videoTestimonyReceived = v1;
  if (v2 !== undefined) payload.textTestimonyReceived = v2;
  if (v3 !== undefined) payload.wantToContributeAsTrainer = v3;
  if (testimony.textTestimonyContent) payload.textTestimonyContent = testimony.textTestimonyContent;
  if (testimony.feedbackToImproveTraining) {
    payload.feedbackToImproveTraining = testimony.feedbackToImproveTraining;
  }
  return Object.keys(payload).length > 0 ? payload : null;
}

export async function resolveCompanyIdForUpdate(
  companyNameOrId,
  companyOptions,
  refreshCompanyOptions
) {
  if (!companyNameOrId || String(companyNameOrId).trim() === '') return null;
  if (/^\d+$/.test(String(companyNameOrId))) return String(companyNameOrId);

  const matched = (companyOptions || []).find((opt) => {
    const optionValue = typeof opt === 'object' ? opt.value : opt;
    return optionValue.toLowerCase() === String(companyNameOrId).trim().toLowerCase();
  });
  if (matched) {
    return String(typeof matched === 'object' ? matched.id : matched);
  }

  const response = await mastersAPI.createCompany({ companyName: String(companyNameOrId).trim() });
  if (response.data?.id) {
    if (refreshCompanyOptions) await refreshCompanyOptions();
    return String(response.data.id);
  }
  return null;
}

/**
 * Shared save path for Edit User page and Filter Students inline edit.
 */
export async function executeAdminUserFormUpdate({
  userId,
  formData,
  companyOptions,
  refreshCompanyOptions,
}) {
  let companyId = null;
  if (formData.placement?.companyName) {
    companyId = await resolveCompanyIdForUpdate(
      formData.placement.companyName,
      companyOptions,
      refreshCompanyOptions
    );
    if (!companyId && String(formData.placement.companyName).trim() !== '') {
      throw new Error(`Failed to resolve company: ${formData.placement.companyName}`);
    }
  }

  const payload = buildAdminUserUpdatePayload(formData, { companyId });
  await usersAPI.update(userId, payload);

  const irPayload = buildInterviewReadinessPayload(formData.interviewReadiness);
  if (irPayload && formData.interviewReadiness?.enrollmentId) {
    await interviewReadinessAPI.upsert(
      String(formData.interviewReadiness.enrollmentId),
      irPayload
    );
  }

  const testimonyPayload = buildTestimonyPayload(formData.testimony);
  if (testimonyPayload && formData.testimony?.enrollmentId) {
    await testimoniesAPI.upsert({
      enrollmentId: formData.testimony.enrollmentId,
      ...testimonyPayload,
    });
  }

  if (Array.isArray(formData.additionalEnrollments)) {
    for (const en of formData.additionalEnrollments) {
      if (!en?.id) continue;
      const patch = {};
      if (en.batchStatus) patch.batchStatus = en.batchStatus;
      if (en.placementStatus) patch.placementStatus = en.placementStatus;
      if (en.attendancePercentage !== '' && en.attendancePercentage != null) {
        patch.attendancePercentage = parseFloat(en.attendancePercentage);
      }
      if (en.courseCompletionDate) patch.courseCompletionDate = en.courseCompletionDate;
      if (en.remarks !== undefined) patch.remarks = en.remarks;
      if (Object.keys(patch).length > 0) {
        await enrollmentsAPI.update(String(en.id), patch);
      }
    }
  }

  return payload;
}
